import type { MarkReveal, MarkRhythm, MarkTone } from './KanameMark';
import { FAN_FULL, FAN_LOCKED, FAN_MISSING, fanOf, petalsFor, type Petals } from './petals';
import { BLOOM_QUICK, bloomEnd } from './schedule';

/*
  Словарь знака-состояния (R42 «Знак ждёт за рамку, заглушка — за данные», spec-2 §0): состояние → поза
  веера, тон и ритм ядра. Один словарь для медальона во вкладке и сцены (StateMark), а через них — для
  StateFrame, сцены первой загрузки, падения интерфейса и галереи. Место «слова» одно — лепесток на
  12 часах (GAP_PETAL). Ничего не крутится: знак-загрузчик шаговый — один лепесток на пройденный шаг.

  Тон и ритм несёт только живое ядро сцены (там, где оболочки ещё нет или уже нет); в медальоне ядра нет —
  кольцо, и тон читается его цветом и толщиной. Знак в главном меню состояний не показывает.
*/

export type MarkStateKind = 'loading' | 'missing' | 'locked' | 'failed' | 'crashed' | 'offline';

export interface MarkState {
  /** Название состояния словом — для галереи. */
  label: string;
  /** Поза веера: готовый веер или по шагам — сколько пройдено (веер стоит на достигнутом). */
  petals: Petals | 'steps';
  /** При появлении веер раскрывается быстрым раскрытием; иначе встаёт сразу — остальное работает. */
  bloom: boolean;
  /** Промежуточная поза раскрытия: «нет доступа» — сначала весь веер, потом пластина гаснет. */
  via?: Petals;
  /** Тон ядра; у медальона — цвет кольца (сбой — ещё и толще). */
  tone: MarkTone;
  /** Ритм живого ядра на сцене. */
  rhythm: MarkRhythm;
  /** Живое ядро сцены даёт одну тревогу, входя в состояние. */
  alarm: boolean;
}

export const MARK_STATES: Record<MarkStateKind, MarkState> = {
  // Сборка: лепестки выходят по шагам, шестой — только на «готово».
  loading: { label: 'Сборка', petals: 'steps', bloom: false, tone: 'brand', rhythm: 'calm', alarm: false },
  // Не найдено: «пропущенный такт» — в такт слота 3 приходит только пунктирный контур.
  missing: {
    label: 'Не найдено',
    petals: FAN_MISSING,
    bloom: true,
    tone: 'brand',
    rhythm: 'calm',
    alarm: false,
  },
  // Нет доступа: пластина выходит в полном веере и гаснет.
  locked: {
    label: 'Нет доступа',
    petals: FAN_LOCKED,
    via: FAN_FULL,
    bloom: true,
    tone: 'brand',
    rhythm: 'calm',
    alarm: false,
  },
  // Не открылось: веер стоит на достигнутом шаге, ядро краснеет скачком и дышит вдвое чаще.
  failed: {
    label: 'Не открылось',
    petals: 'steps',
    bloom: false,
    tone: 'danger',
    rhythm: 'quick',
    alarm: true,
  },
  // Сбой: веер цел и неподвижен; живое ядро после тревоги останавливается.
  crashed: { label: 'Сбой', petals: FAN_FULL, bloom: false, tone: 'danger', rhythm: 'still', alarm: true },
  // Нет связи: веер «затаил дыхание», ядро в тоне предупреждения.
  offline: {
    label: 'Нет связи',
    petals: 'steps',
    bloom: false,
    tone: 'warning',
    rhythm: 'still',
    alarm: false,
  },
};

/** «Нет доступа»: пауза между посадкой веера и тем, как гаснет пластина. */
export const LOCK_PAUSE_MS = 200;
/** Когда гаснет пластина «нет доступа» — от начала раскрытия. */
export const LOCK_DIM_AT = bloomEnd(BLOOM_QUICK) + LOCK_PAUSE_MS;

/** Поза веера состояния: по шагам — сколько лепестков за done из total (без шагов — веер целый). */
export function statePetals(kind: MarkStateKind, done = 0, total = 0): Petals {
  const p = MARK_STATES[kind].petals;
  return p === 'steps' ? fanOf(petalsFor(done, total)) : p;
}

/** Как выходят лепестки: раскрытие, шаг загрузки или досадка на «готово». */
export function stateReveal(kind: MarkStateKind, done = 0, total = 0): MarkReveal {
  if (MARK_STATES[kind].bloom) return 'bloom';
  return kind === 'loading' && done >= total ? 'snap' : 'step';
}
